// Vaste lijst mijlpalen. Alles wordt afgeleid uit het logboek: `achievedAt` is het vroegste moment waarop
// de voorwaarde waar werd, dus bewerken of verwijderen van logs verandert de uitkomst vanzelf.
import { startOfWeek, toPlayEntries, type PlayEntry } from './goals';
import { toDateKey } from './playtime';
import type { LogEntry } from './types';

export type MilestoneIcon = 'flag' | 'medal' | 'flame' | 'layout' | 'compass' | 'clock' | 'tree';

export interface MilestoneProgress {
  current: number;
  target: number;
}

export interface Milestone {
  id: string;
  title: string;
  description: string;
  /** Naam van het lucide-icoon dat de UI toont. */
  icon: MilestoneIcon;
  achievedAt(logs: readonly LogEntry[]): Date | null;
  progress(logs: readonly LogEntry[]): MilestoneProgress;
}

export interface MilestoneStatus {
  milestone: Milestone;
  achievedAt: Date | null;
  progress: MilestoneProgress;
}

const OUTDOOR_WEEK_MINUTES = 120;

interface Analysis {
  at: Date | null;
  current: number;
}

/** Hulp: bepaalt per mijlpaal het bereikmoment (of null) en de huidige waarde, in één pas over de oudste-eerst lijst. */
type Analyzer = (entries: PlayEntry[], target: number) => Analysis;

function result(at: Date | null, current: number, target: number): Analysis {
  return { at, current: at ? target : Math.min(current, target) };
}

const countAnalyzer: Analyzer = (entries, target) =>
  result(entries.length >= target ? entries[target - 1].at : null, entries.length, target);

/** Lengte van de langste reeks opeenvolgende dagen; moment = eerste missie op de dag waarop de reeks `target` haalt. */
const streakAnalyzer: Analyzer = (entries, target) => {
  const firstOfDay = new Map<string, { at: Date; dayNumber: number }>();
  for (const e of entries) {
    const key = toDateKey(e.at);
    if (!firstOfDay.has(key)) {
      // Dagnummer via UTC-datum: onafhankelijk van zomertijd.
      const dayNumber = Math.round(Date.UTC(e.at.getFullYear(), e.at.getMonth(), e.at.getDate()) / 86_400_000);
      firstOfDay.set(key, { at: e.at, dayNumber });
    }
  }
  let run = 0;
  let best = 0;
  let prev = Number.NaN;
  let reached: Date | null = null;
  for (const day of firstOfDay.values()) {
    run = day.dayNumber === prev + 1 ? run + 1 : 1;
    prev = day.dayNumber;
    best = Math.max(best, run);
    if (reached === null && run >= target) reached = day.at;
  }
  return result(reached, best, target);
};

const allCategoriesAnalyzer: Analyzer = (entries, target) => {
  const seen = new Set<string>();
  let reached: Date | null = null;
  for (const e of entries) {
    seen.add(e.category);
    if (seen.size >= target) {
      reached = e.at;
      break;
    }
  }
  return result(reached, seen.size, target);
};

const distinctActivitiesAnalyzer: Analyzer = (entries, target) => {
  const seen = new Set<string>();
  let reached: Date | null = null;
  for (const e of entries) {
    seen.add(e.name);
    if (reached === null && seen.size >= target) reached = e.at;
  }
  return result(reached, seen.size, target);
};

const totalMinutesAnalyzer: Analyzer = (entries, target) => {
  let sum = 0;
  let reached: Date | null = null;
  for (const e of entries) {
    sum += e.minutes;
    if (reached === null && sum >= target) reached = e.at;
  }
  return result(reached, sum, target);
};

/** Minuten buiten in één week (maandag t/m zondag): beste week telt. */
const outdoorWeekAnalyzer: Analyzer = (entries, target) => {
  const perWeek = new Map<string, number>();
  let best = 0;
  let reached: Date | null = null;
  for (const e of entries) {
    if (!e.outdoor) continue;
    const key = toDateKey(startOfWeek(e.at));
    const sum = (perWeek.get(key) ?? 0) + e.minutes;
    perWeek.set(key, sum);
    best = Math.max(best, sum);
    if (reached === null && sum >= target) reached = e.at;
  }
  return result(reached, best, target);
};

function define(
  id: string,
  title: string,
  description: string,
  icon: MilestoneIcon,
  target: number,
  analyze: Analyzer,
): Milestone {
  const run = (logs: readonly LogEntry[]) => analyze(toPlayEntries(logs), target);
  return {
    id,
    title,
    description,
    icon,
    achievedAt: (logs) => run(logs).at,
    progress: (logs) => ({ current: run(logs).current, target }),
  };
}

export const MILESTONES: readonly Milestone[] = [
  define('first-mission', 'Eerste missie', 'Rond je eerste missie af.', 'flag', 1, countAnalyzer),
  define('missions-10', '10 missies voltooid', 'Rond 10 missies af.', 'medal', 10, countAnalyzer),
  define('missions-25', '25 missies voltooid', 'Rond 25 missies af.', 'medal', 25, countAnalyzer),
  define('missions-50', '50 missies voltooid', 'Rond 50 missies af.', 'medal', 50, countAnalyzer),
  define('missions-100', '100 missies voltooid', 'Rond 100 missies af.', 'medal', 100, countAnalyzer),
  define('streak-3', '3 dagen op rij', 'Speel 3 dagen achter elkaar.', 'flame', 3, streakAnalyzer),
  define('streak-5', '5 dagen op rij', 'Speel 5 dagen achter elkaar.', 'flame', 5, streakAnalyzer),
  define('streak-7', '7 dagen op rij', 'Speel 7 dagen achter elkaar.', 'flame', 7, streakAnalyzer),
  define('all-categories', 'Alle vier de soorten', 'Doe een missie in elk van de vier hoofdcategorieën.', 'layout', 4, allCategoriesAnalyzer),
  define('activities-10', '10 verschillende activiteiten', 'Probeer 10 verschillende activiteiten.', 'compass', 10, distinctActivitiesAnalyzer),
  define('hours-10', '10 uur gespeeld', 'Speel in totaal 10 uur.', 'clock', 600, totalMinutesAnalyzer),
  define('hours-25', '25 uur gespeeld', 'Speel in totaal 25 uur.', 'clock', 1500, totalMinutesAnalyzer),
  define('outdoor-week', 'Een week lekker buiten', 'Speel in één week minstens 2 uur buiten.', 'tree', OUTDOOR_WEEK_MINUTES, outdoorWeekAnalyzer),
];

/** Alle mijlpalen met status, in vaste volgorde. */
export function evaluateMilestones(logs: readonly LogEntry[]): MilestoneStatus[] {
  return MILESTONES.map((milestone) => ({
    milestone,
    achievedAt: milestone.achievedAt(logs),
    progress: milestone.progress(logs),
  }));
}

/** Tot drie nog niet behaalde mijlpalen, het dichtst bij af eerst (bij gelijkstand: vaste volgorde). */
export function nextUp(logs: readonly LogEntry[]): MilestoneStatus[] {
  return evaluateMilestones(logs)
    .filter((s) => s.achievedAt === null)
    .map((s, index) => ({ s, index, fraction: s.progress.current / s.progress.target }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index)
    .slice(0, 3)
    .map((x) => x.s);
}

/** Id's van alle nu behaalde mijlpalen. */
export function achievedIds(logs: readonly LogEntry[]): string[] {
  return evaluateMilestones(logs)
    .filter((s) => s.achievedAt !== null)
    .map((s) => s.milestone.id);
}
